import { makeAutoObservable, runInAction, toJS, computed } from 'mobx'
import { string } from 'yup'
import InputStore from '../../stores/InputStore'
import DriverService from '../../services/DriverService'
import { MAINTENANCE_MODE } from '../../routing/paths'

class CaselessSearchStore {
  constructor () {
    this.operation = new InputStore(string().required('Operation is required'))
    this.operations = []
    this.licensePlateNumber = new InputStore(string().required('License number is required'))
    this.licensePlateState = new InputStore(string().required('License state is required'))
    this.isLoading = false
    this.serverError = ''

    makeAutoObservable(this)

    this.driverService = new DriverService()
  }

  async loadOperations () {
    const operations = await this.driverService.listCaselessOperations();
    runInAction(() => {
      this.operations = operations
    })
  }

  changeLicensePlateNumber (value) {
    this.licensePlateNumber.setValue(value)
  }

  changeLicensePlateState (value) {
    this.licensePlateState.setValue(value)
  }

  changeOperation (value) {
    this.operation.setValue(value)
  }

  get selectedOperationLogo () {
    const a = this.operations.filter((operation) => {
      return operation.value == this.operation?.value
    })[0]
    return a?.logo
  }

  clearErrors () {
    this.serverError = ''
    this.licensePlateNumber.clearError()
    this.licensePlateState.clearError()
    this.operation.clearError()
  }

  async validate () {
    this.clearErrors()
    let isValid = true

    if (!(await this.operation.validate())) {
      isValid = false
    }

    if (!(await this.licensePlateNumber.validate())) {
      isValid = false
    }

    if (!(await this.licensePlateState.validate())) {
      isValid = false
    }

    return isValid
  }

  async search () {
    if (this.isLoading) {
      return false
    }
    this.isLoading = true
    if (await this.validate()) {
      try {
        const license = await this.driverService.caselessSearch(
          this.operation.value,
          this.licensePlateNumber.value,
          this.licensePlateState.value
        )

        runInAction(() => {
          this.isLoading = false
        })

        return license
      } catch (e) {
        runInAction(() => {
          // 409 means repeated request. Handles a firefox bug that sends request twice
          if (e?.response?.status === 409) {
            return
          }
          if (e?.response?.status === 404) {
            this.serverError = "No results were found. Please confirm your information is correct and resubmit."
          } else if (e?.response?.status === 503) {
            window.location.href = `${MAINTENANCE_MODE}?phone=${e?.response?.data.phone}`
          } else {
            const displayedError = this.parseRequestErrors(e.response?.data?.errors || {})

            if (!displayedError) {
              this.serverError =
                'Something went wrong, please check the provided data and try again.'
            }
          }

          this.isLoading = false
        })
      }
    }
    runInAction(() => {
      this.isLoading = false
    })
    return false
  }

  parseRequestErrors (messages) {
    const keys = Object.keys(messages)
    let displayedError = false

    keys.forEach((key) => {
      const [error] = messages[key]

      switch (key) {
        case 'operation_id':
          this.operation.setError(true, error)
          displayedError = true
          break

        case 'license_number':
          this.licensePlateNumber.setError(true, error)
          displayedError = true
          break

        case 'license_state':
          this.licensePlateState.setError(true, error)
          displayedError = true
          break

        default:
          break
      }
    })

    return displayedError
  }
}

export default CaselessSearchStore
